import React from 'react';
import { BookOpen, Plus, Minus, User, Phone, Trash2, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

/**
 * Yeni Veresiye Kaydı dialogu.
 * Kompleks UI ve çok prop alır — VeresiyeManagement.js'ten ayrıldı.
 */
const VeresiyeNewEntryDialog = ({
  open, onClose,
  newName, setNewName, newPhone, setNewPhone, newNotes, setNewNotes,
  cart, cartTotal, addToCart, updateCartQty, removeFromCart,
  discountPct, setDiscountPct, discountValue, finalTotal,
  menuSearch, setMenuSearch, menuTab, setMenuTab,
  filteredMenu, menuCategories, filteredBev, bevCategories, cateringItems,
  loading, onSubmit, prefillName
}) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>Yeni Veresiye Kaydi</DialogTitle></DialogHeader>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: Customer + Menu */}
        <div className="space-y-3">
          {/* Customer Info */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-charcoal/50" />
              <Input placeholder="Musteri Adi *" value={newName} onChange={e => setNewName(e.target.value)}
                disabled={prefillName} className={prefillName ? 'bg-stone-50' : ''} data-testid="veresiye-new-name" />
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-charcoal/50" />
              <Input placeholder="Telefon (opsiyonel)" value={newPhone} onChange={e => setNewPhone(e.target.value)}
                disabled={prefillName} data-testid="veresiye-new-phone" />
            </div>
          </div>

          {/* Menu Tabs */}
          <div className="flex gap-1">
            {[
              { id: 'alakart', label: 'A La Carte' },
              { id: 'beverages', label: 'Icecekler' },
              { id: 'catering', label: 'Gunluk Menu' }
            ].map(tab => (
              <button key={tab.id} onClick={() => setMenuTab(tab.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${menuTab === tab.id ? 'bg-amber-700 text-white' : 'bg-stone-100 text-charcoal/60 hover:bg-stone-200'}`}>
                {tab.label}
              </button>
            ))}
          </div>

          <Input placeholder="Urun ara..." value={menuSearch} onChange={e => setMenuSearch(e.target.value)}
            className="text-sm" data-testid="veresiye-menu-search" />

          {/* Menu Items */}
          <div className="max-h-[300px] overflow-y-auto space-y-2 pr-1">
            {menuTab === 'alakart' && menuCategories.map(cat => (
              <div key={cat}>
                <p className="text-xs font-semibold text-charcoal/50 uppercase mb-1">{cat}</p>
                <div className="grid grid-cols-2 gap-1">
                  {filteredMenu.filter(m => m.category === cat).map(item => (
                    <button key={item.name} onClick={() => addToCart({ ...item, item_type: 'alakart' })}
                      className="bg-stone-50 rounded-lg p-2 text-left hover:bg-amber-50 transition-all text-sm active:scale-95 border border-stone-100"
                      data-testid={`veresiye-item-${item.name.replace(/\s+/g, '-')}`}>
                      <p className="font-medium text-charcoal truncate">{item.name}</p>
                      <p className="text-amber-700 font-bold text-xs">€{item.price.toFixed(2)}</p>
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {menuTab === 'beverages' && bevCategories.map(cat => (
              <div key={cat}>
                <p className="text-xs font-semibold text-charcoal/50 uppercase mb-1">{cat}</p>
                <div className="grid grid-cols-2 gap-1">
                  {filteredBev.filter(b => b.category === cat).map(item => (
                    <button key={item.id} onClick={() => addToCart({ ...item, item_type: 'beverage' })}
                      className="bg-stone-50 rounded-lg p-2 text-left hover:bg-amber-50 transition-all text-sm active:scale-95 border border-stone-100">
                      <p className="font-medium text-charcoal truncate">{item.name}</p>
                      <p className="text-amber-700 font-bold text-xs">€{item.price.toFixed(2)}</p>
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {menuTab === 'catering' && (
              cateringItems.length === 0 ? (
                <p className="text-charcoal/40 text-sm text-center py-4">Bugun icin gunluk menu yok</p>
              ) : (
                <div className="grid grid-cols-2 gap-1">
                  {cateringItems.map(item => (
                    <button key={item.id} onClick={() => addToCart(item)}
                      className="bg-stone-50 rounded-lg p-2 text-left hover:bg-amber-50 transition-all text-sm active:scale-95 border border-stone-100">
                      <p className="font-medium text-charcoal truncate">{item.name}</p>
                      <p className="text-xs text-charcoal/50">{item.category}</p>
                      <p className="text-amber-700 font-bold text-xs">€{item.price.toFixed(2)}</p>
                    </button>
                  ))}
                </div>
              )
            )}
          </div>
        </div>

        {/* Right: Cart */}
        <div className="space-y-3">
          <div className="bg-stone-50 rounded-xl p-3 border border-stone-200">
            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-stone-200">
              <FileText className="w-4 h-4 text-amber-700" />
              <span className="font-semibold text-charcoal text-sm">Veresiye Listesi ({cart.length})</span>
            </div>

            {cart.length === 0 ? (
              <p className="text-charcoal/40 text-center py-6 text-sm">Urun ekleyin</p>
            ) : (
              <div className="space-y-1 max-h-[200px] overflow-y-auto mb-2">
                {cart.map(c => {
                  const key = `${c.name}-${c.item_type}`;
                  return (
                    <div key={key} className="flex items-center gap-2 py-1.5 border-b border-stone-100 last:border-0">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-charcoal truncate">{c.name}</p>
                        <p className="text-xs text-charcoal/50">€{c.price.toFixed(2)}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => updateCartQty(key, -1)} className="w-6 h-6 rounded-full bg-white flex items-center justify-center border border-stone-200 hover:bg-stone-100">
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-5 text-center font-bold text-sm">{c.quantity}</span>
                        <button onClick={() => updateCartQty(key, 1)} className="w-6 h-6 rounded-full bg-white flex items-center justify-center border border-stone-200 hover:bg-stone-100">
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <span className="font-bold text-sm w-14 text-right">€{(c.price * c.quantity).toFixed(2)}</span>
                      <button onClick={() => removeFromCart(key)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="border-t border-stone-200 pt-2 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-charcoal/70">Ara Toplam</span>
                <span className="font-medium" data-testid="veresiye-subtotal">€{cartTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <label className="text-charcoal/70 flex items-center gap-1.5" htmlFor="veresiye-discount-input">
                  Indirim
                  <span className="text-xs text-charcoal/50">(%)</span>
                </label>
                <div className="flex items-center gap-1.5">
                  <input
                    id="veresiye-discount-input"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    max="100"
                    step="0.5"
                    value={discountPct}
                    onChange={e => setDiscountPct(e.target.value)}
                    onFocus={e => e.target.select()}
                    className="w-16 text-right rounded-md border border-stone-300 bg-white px-2 py-1 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-600/40"
                    data-testid="veresiye-discount-pct"
                  />
                  <span className="text-charcoal/60 text-sm">%</span>
                  <span className="font-medium text-red-600 w-16 text-right" data-testid="veresiye-discount-amount">
                    -€{discountValue.toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="flex justify-between font-bold pt-1 border-t border-stone-100">
                <span>Genel Toplam</span>
                <span className="text-amber-700 text-lg" data-testid="veresiye-cart-total">€{finalTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <Textarea placeholder="Not (opsiyonel)" value={newNotes} onChange={e => setNewNotes(e.target.value)}
            className="min-h-[60px]" data-testid="veresiye-new-notes" />

          <Button onClick={onSubmit} disabled={loading || cart.length === 0}
            className="w-full bg-amber-700 text-white hover:bg-amber-600 py-5 font-semibold disabled:opacity-50"
            data-testid="veresiye-submit-btn">
            {loading ? 'Kaydediliyor...' : <><BookOpen className="w-5 h-5 mr-2" />Veresiye Kaydet</>}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export default VeresiyeNewEntryDialog;
